<script setup lang="ts">
import { useData } from 'vike-vue/useData'
import { ref, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { QuestionPageData } from './+data'
import type { UiFaqListProps } from '@afs/design-system/components/molecules/UiFaqList/interface.js'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted } from '@/composables/useGtmTag'

const linksRefs: Ref<HTMLDivElement | null> = ref(null)

const { t } = useI18n()
const data = useData<QuestionPageData>()
useFormatSeoContent(data.question.seoTitle, data.question.subTitle)
useGtmOnPageMounted('other')

const breadcrumb = {
  ariaLabel: t('breadcrumb.ariaLabel'),
  items: [
    { text: t('page.accueil'), to: '/' },
    { text: t('page.assistance'), to: '/faq' },
    {
      text:
        data.question?.breadcrumbTitle && data.question.breadcrumbTitle.length > 40
          ? `${data.question.breadcrumbTitle.slice(0, 40)}...`
          : data.question?.breadcrumbTitle,
    },
  ],
}
</script>

<template>
  <div
    v-if="data.question"
    class="FAQDetails"
  >
    <molecules-ui-breadcrumb
      v-bind="breadcrumb"
      class="FAQ__breadcrumb"
    />
    <div class="container my-8">
      <section class="grid g-6 mb-8">
        <aside
          ref="linksRefs"
          class="FAQDetails__aside pb-2 g-2"
        >
          <atoms-ui-link
            v-for="link in data.question.tags"
            v-bind="link"
            :key="link.id"
            :class="data.question.tag === link.text ? 'FAQDetails__link--active' : ''"
          />
        </aside>
        <div class="FAQDetails__content">
          <div class="FAQDetails__head color-blue-01">
            <h1 class="mb-2 heading-1">{{ data.question.title }}</h1>
            <p class="paragraph-1">{{ data.question.subTitle }}</p>
          </div>
          <molecules-ui-rich-text
            v-if="data.question.content"
            :content="data.question.content"
          />
          <molecules-ui-highlight
            v-if="data.question.blockCta"
            v-bind="data.question.blockCta"
            class="FAQ__highlight"
          />
        </div>
      </section>
      <section class="FAQDetails__bottom grid gy-7">
        <div
          v-if="data.question.blockFaq.faq"
          class="FAQDetails__bottom__left"
        >
          <h2 class="FAQDetails__bottom__title px-4 heading-2 color-blue-01">{{ data.question.blockFaq.title }}</h2>
          <molecules-ui-faq-list v-bind="data.question.blockFaq.faq as UiFaqListProps" />
        </div>
        <div
          v-if="data.question.blockContact"
          class="FAQDetails__bottom__right"
        >
          <molecules-ui-contact v-bind="data.question.blockContact" />
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/colors';
@use 'config/spaces';
@use 'utilities/mq';

@forward 'utilities/text';

.FAQDetails__aside {
  grid-column: span 4;
  display: flex;

  @include mq.media('<medium') {
    margin-left: calc(var(--layout-padding) * -1);
    margin-right: calc(var(--layout-padding) * -1);
    padding-left: var(--layout-padding);
    padding-right: var(--layout-padding);
    overflow-y: auto;
  }

  @include mq.media('>=medium') {
    grid-column: 1 / span 4;
    flex-direction: column;
  }

  .Link {
    flex-shrink: 0;
    min-height: unset;
  }
}

.FAQDetails__content {
  grid-column: span 4;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  row-gap: map.get(spaces.$sizes, '7');

  @include mq.media('>=medium') {
    grid-column: span 8;
    grid-row: span 2;
    row-gap: map.get(spaces.$sizes, '6');
  }
}

.FAQDetails__bottom {
  align-items: end;
}

.FAQDetails__bottom__left {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 1 / span 7;
  }
}

.FAQDetails__bottom__title {
  margin-bottom: map.get(spaces.$sizes, '6');

  @include mq.media('<medium') {
    margin-bottom: map.get(spaces.$sizes, '5');
    text-align: center;
  }
}

.FAQDetails__bottom__right {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: span 5;
  }
}

.FAQDetails__link--active {
  color: map.get(colors.$basics, 'white');
  background-color: map.get(colors.$blue, '01');

  @include mq.media('<medium') {
    order: -1;
  }
}

.FAQ__highlight {
  :deep(.Highlight__title) {
    @extend .heading-3;
  }

  :deep(.Highlight__description) {
    @extend .paragraph-3;
  }

  @include mq.media('>=medium') {
    align-items: center;
    flex-direction: row;

    :deep(.Button) {
      align-self: center;
      flex-shrink: 0;
      margin-top: 0;
    }
  }
}
</style>
